import hitsUrl from "../sounds/die-hit.mp3";
import { THROW_STAGGER_MS, THROWN_DICE } from "./landing";
import { diceVolume } from "./preferences";

// A first touch's peak at each volume step of the dice box: low, medium, high.
const HIT_GAINS = [0.11, 0.3, 0.7];
// The second bounce, as a share of the first. 0 silences it.
const SECOND_SHARE = 0.4;
const SECOND_RATE = 1.06;

// Another player's roll: how much quieter, how dull, and how much of it goes into the room.
const FAR_DB = -4;
const FAR_CUTOFF_HZ = 5000;
const FAR_SEND = 0.3;
const ROOM: [delay: number, feedback: number][] = [
	[0.037, 0.55],
	[0.053, 0.5],
	[0.071, 0.45],
	[0.097, 0.4],
];

// die-hit.mp3 holds nine clips, one per 0.2 s slot.
const CLIPS = 9;
const SLOT_S = 0.2;
const CLIP_S = 0.16;
const LOUD = 0.01;
const BUDGET = 5;
const BUDGET_MS = 60;
// Longer than any roll plays: a roll cancelled mid-flip sends no `done`.
const FLIP_KEPT_MS = 10_000;
const DIE_RATE: Record<number, number> = { 4: 1.18, 6: 1, 8: 1.08, 10: 1.05, 12: 0.95, 20: 0.9, 100: 1.05 };
const PANS = [0, -0.3, 0.3, -0.15, 0.15];

type Detail = { key: string; own: boolean; play?: number; mode?: string; dice?: number; index?: number; sides?: number; bounce?: number };
type Hit = { own: boolean; index: number; sides?: number; share?: number; rate?: number };
type Graph = { ctx: AudioContext; out: AudioNode; far?: AudioNode };

let started = false;
let graph: Graph | null = null;
let buffer: AudioBuffer | null = null;
let lead = 0;
let loading: Promise<void> | null = null;
let last = 0;
let recent: number[] = [];
let previews = 0;

function audio(): Graph | null {
	if (graph) return graph;
	const Context = globalThis.AudioContext ?? (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
	if (!Context) return null;
	let ctx: AudioContext;
	try {
		ctx = new Context();
	} catch {
		// A browser that refuses a context costs the sound, never the roll.
		return null;
	}
	// The iPhone's silent switch then mutes the dice.
	const session = (navigator as { audioSession?: { type: string } }).audioSession;
	if (session) session.type = "ambient";
	const out = ctx.createDynamicsCompressor();
	out.threshold.value = -10;
	out.knee.value = 6;
	out.ratio.value = 12;
	out.attack.value = 0.003;
	out.release.value = 0.15;
	out.connect(ctx.destination);
	ctx.resume().catch(() => {});
	graph = { ctx, out };
	return graph;
}

function lowpass(ctx: AudioContext, hz: number) {
	const filter = ctx.createBiquadFilter();
	filter.type = "lowpass";
	filter.frequency.value = hz;
	return filter;
}

// The chain another player's roll plays through: quieter, duller, and sent into a tiny room.
function distant(held: Graph) {
	const { ctx, out } = held;
	const near = ctx.createGain();
	near.gain.value = 10 ** (FAR_DB / 20);
	const dull = lowpass(ctx, FAR_CUTOFF_HZ);
	const send = ctx.createGain();
	send.gain.value = FAR_SEND;
	const sum = ctx.createGain();
	sum.gain.value = 0.35;
	near.connect(dull);
	dull.connect(out);
	dull.connect(send);
	sum.connect(out);
	for (const [time, feedback] of ROOM) {
		const delay = ctx.createDelay();
		delay.delayTime.value = time;
		const again = ctx.createGain();
		again.gain.value = feedback;
		const soft = lowpass(ctx, 2400);
		send.connect(delay);
		delay.connect(sum);
		delay.connect(again);
		again.connect(soft);
		soft.connect(delay);
	}
	held.far = near;
	return near;
}

// Shared by every roll; a failure clears it so the next roll retries.
function load() {
	const held = audio();
	if (!held || loading) return;
	loading = fetch(hitsUrl)
		.then((response) => response.arrayBuffer())
		.then((bytes) => held.ctx.decodeAudioData(bytes))
		.then((decoded) => {
			// MP3 decoding adds a leading silence that differs by browser.
			const first = decoded.getChannelData(0).findIndex((sample) => Math.abs(sample) > LOUD);
			lead = Math.max(first, 0) / decoded.sampleRate;
			buffer = decoded;
		})
		.catch(() => {
			loading = null;
		});
}

function hit({ own, index, sides = 0, share = 1, rate = 1 }: Hit) {
	const gain = share * (HIT_GAINS[diceVolume() - 1] ?? 0);
	if (!gain) return;
	const held = audio();
	load();
	// A suspended context's clock stands still: a hit started now would wait and burst out at the next gesture.
	if (!held || !buffer || held.ctx.state !== "running") return;
	const now = Date.now();
	recent = recent.filter((at) => now - at < BUDGET_MS);
	if (recent.length >= BUDGET) return;
	recent.push(now);

	const { ctx } = held;
	last = (last + 1 + Math.floor(Math.random() * (CLIPS - 1))) % CLIPS;
	const speed = rate * (DIE_RATE[sides] ?? 1) * (0.96 + Math.random() * 0.08);
	const peak = gain * 10 ** ((Math.random() * 3 - 2) / 20);
	const at = ctx.currentTime;
	const span = CLIP_S / speed;

	const source = ctx.createBufferSource();
	source.buffer = buffer;
	source.playbackRate.value = speed;
	const envelope = ctx.createGain();
	envelope.gain.setValueAtTime(0, at);
	envelope.gain.linearRampToValueAtTime(peak, at + 0.003);
	envelope.gain.setValueAtTime(peak, at + span - 0.012);
	envelope.gain.linearRampToValueAtTime(0, at + span);
	source.connect(envelope);
	let tail: AudioNode = envelope;
	if (ctx.createStereoPanner) {
		const panner = ctx.createStereoPanner();
		panner.pan.value = PANS[index % PANS.length];
		envelope.connect(panner);
		tail = panner;
	}
	tail.connect(own ? held.out : (held.far ?? distant(held)));
	source.start(at, Math.max(0, lead + last * SLOT_S - 0.002), CLIP_S);
}

// A roll is heard when the viewer rolled it or it is drawn here: a card or feed entry carries data-state,
// the stage layer that forwards a thrown roll's events does not.
const seen = (event: Event, { own }: Detail) => own || Boolean((event.target as HTMLElement | null)?.dataset?.state);

// Called inside the gesture that makes a roll, before anything is awaited: Safari lets a context start only there.
export function primeDiceSound() {
	load();
}

// The dice box plays one hit as its volume changes, so the new step is heard. Only the latest press sounds, once the
// clip has loaded and the context, which the press may have just woken, runs.
export function previewDiceSound() {
	load();
	const press = ++previews;
	if (!graph) return;
	Promise.all([loading, graph.ctx.resume().catch(() => {})]).then(() => {
		if (press === previews) hit({ own: true, index: 0 });
	});
}

// Sound is fired by the dice animation's events, never by a clock of its own.
export function startDiceSound() {
	if (started) return;
	started = true;
	// The card playbacks flipping in place, by their `play`, and when each began.
	const flipping = new Map<number, number>();
	const listen = (name: string, run: (event: Event, detail: Detail) => void) =>
		document.addEventListener(name, (event) => run(event, (event as CustomEvent<Detail>).detail));

	// Browsers keep a context silent until a gesture, and Safari wants the resume inside one. Nothing is created
	// until the dice box is touched or a roll plays, so a page nobody rolls on never opens an audio device.
	const wake = (event: Event) => {
		if (graph) {
			if (graph.ctx.state !== "running") graph.ctx.resume().catch(() => {});
		} else if ((event.target as Element | null)?.closest?.('[data-controller~="dice-roller"]')) load();
	};
	for (const type of ["pointerdown", "pointerup", "keydown"]) document.addEventListener(type, wake, { capture: true, passive: true });

	listen("dice-landing:start", (event, detail) => {
		load();
		if (!seen(event, detail)) return;
		const now = Date.now();
		for (const [play, at] of flipping) if (now - at > FLIP_KEPT_MS) flipping.delete(play);
		if (detail.mode === "flip" && detail.play !== undefined) flipping.set(detail.play, now);
		if (detail.mode !== "instant") return;
		for (let index = 0; index < Math.min(detail.dice ?? 0, THROWN_DICE); index++) {
			const play = () => hit({ own: detail.own, index });
			if (index) setTimeout(play, index * THROW_STAGGER_MS);
			else play();
		}
	});
	listen("dice-landing:flip", (_event, detail) => {
		const { index = 0 } = detail;
		if (flipping.has(detail.play) && index < THROWN_DICE) hit({ own: detail.own, index, sides: detail.sides });
	});
	listen("dice-landing:done", (_event, detail) => flipping.delete(detail.play));
	listen("dice-landing:bounce", (_event, detail) => {
		const second = detail.bounce === 2;
		hit({ own: detail.own, index: detail.index ?? 0, sides: detail.sides, share: second ? SECOND_SHARE : 1, rate: second ? SECOND_RATE : 1 });
	});
}
